import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useMutation,
  useQuery,
  useQueryClient,
  useQueries,
} from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  PagesList,
} from '@components';
import { GlobalContext } from '@contexts';
import { datamodelIdFromType } from '@helpers';
import {
  businessQuery,
  datamodelQuery,
  datamodelsQuery,
  siteQuery,
  siteMutation,
  themeQuery,
} from '@queries';
import { ChevronRightIcon } from '@svgs';

import { TemplateSidebar } from '../components/TemplateSidebar/TemplateSidebar.jsx';

import { FallbacksForm } from './FallbacksForm/FallbacksForm.jsx';

export const TemplateFallbacks = () => {
  const { t, i18n } = useTranslation();
  const { businessID, siteID } = useParams();
  const globals = useContext(GlobalContext);
  // query client for triggering invalidations
  const queryClient = useQueryClient();

  // get business
  const { data: businessData, isPending: businessIsPending } = useQuery(
    businessQuery(businessID),
  );

  // get site/template
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  // fetch theme
  const { data: themeData, isPending: themeIsPending } = useQuery(
    themeQuery(siteData?.theme_id, {
      enabled: !!siteData?.theme_id,
    }),
  );

  // get the datamodels list
  const { data: datamodelsData, isPending: datamodelsIsPending } = useQuery(
    datamodelsQuery({
      enabled: !themeIsPending,
    }),
  );

  // get the datamodel ID for site custom fields
  const datamodelID = useMemo(() => {
    return themeData?.datamodel_id
      ? themeData.datamodel_id
      : datamodelsData?.objects
        ? datamodelIdFromType(datamodelsData?.objects, 'site_custom_fields')
        : null;
  }, [themeData, datamodelsData]);

  // get the datamodel
  const { data: datamodelData, isPending: datamodelIsPending } = useQuery(
    datamodelQuery(datamodelID, {
      enabled: !!datamodelID,
    }),
  );

  // get all the related datamodels used on fields
  const relatedDatamodels = useQueries({
    enabled: !datamodelIsPending,
    queries: datamodelData
      ? datamodelData.fields
          .filter((field) => Number.isInteger(field.related_datamodel_id))
          .map((field) => datamodelQuery(field.related_datamodel_id))
      : [],
  });
  // are all datamodel queries finished
  const allFinished = relatedDatamodels.every((query) => query.isSuccess);

  // Posts saved form data to api
  const mutation = useMutation(
    siteMutation(siteID, {
      onSuccess: () => {
        // invalidate the query so it is fetched again
        queryClient.invalidateQueries({
          queryKey: ['sites', 'detail', siteID],
        });
      },
    }),
  );

  return (
    <>
      <Header />
      <BodyWrapper>
        <TemplateSidebar
          businessID={businessID}
          businessData={businessData}
          siteID={siteID}
          siteData={siteData}
        />
        <MainWrapper>
          <div className="sb-main-breadcrumbs">
            <ul className="sb-flex">
              {globals.visible.projectsView && (
                <li className="sb-flex">
                  <Link to={`/dashboard/projects`}>Projects</Link>
                  <ChevronRightIcon />
                </li>
              )}
              <li className="sb-flex">
                {businessIsPending && <span>...</span>}
                {!businessIsPending && (
                  <Link to={`/dashboard/projects/${businessData.id}`}>
                    {businessData.business_name}
                  </Link>
                )}
                <ChevronRightIcon />
              </li>
              <li className="sb-flex">
                <Link to={`/dashboard/projects/${businessID}/templates`}>
                  Templates
                </Link>
                <ChevronRightIcon />
              </li>
              <li className="sb-flex">
                <Link
                  to={`/dashboard/projects/${businessID}/templates/${siteID}`}
                >
                  {siteIsPending && <>...</>}
                  {!siteIsPending && <>{siteData.title}</>}
                </Link>
                <ChevronRightIcon />
              </li>
              <li className="sb-flex">
                <span>{t('Content fallbacks')}</span>
              </li>
            </ul>
          </div>
          <div className="sb-manage-content">
            <h1>{t('Content fallbacks')}</h1>
            {(datamodelIsPending ||
              datamodelsIsPending ||
              siteIsPending ||
              !allFinished) && <Loader className="sb-loader sb-loader-large" />}
            {siteData && datamodelData && datamodelsData && allFinished && (
              <FallbacksForm
                datamodelData={datamodelData}
                mutation={mutation}
                disabled={mutation.isPending}
                relatedDatamodels={relatedDatamodels}
                siteData={siteData}
              />
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
